/* eslint-disable camelcase */
import React, {
    useContext, useMemo, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import apolloClient from 'app/apollo/client';

import { menuDatesState } from 'app/containers/contexts/menuDates.context';
import ROOT_MENU_DISHES_LIST from 'app/graphql/network/menuDishes2';
import { useSelectedFilters } from 'app/connect/connectToSelectedFilters';
import { CONTRY_SUBDIVISION_FRAGMENT } from 'app/graphql/network/fragments/address.fragment';
import {
    allDishesListVar,
    menuDishesIdListVar,
    selectedPeriodVar,
    regionSubdivisionVar,
    maxiBlockContentKeyVar,
} from 'app/apollo/reaction';

import {
    DEFAULT_CATEGORIES,
} from 'app/const/categories';

import {
    selectDishes as selectDishesByFilter,
    getDishesFromBasketSections,
} from 'app/utils/dish';
import { useBasketQueryLocate } from 'app/hooks/useBasketQueryLocate';
import { useMenuPeriodsData } from 'app/hooks/accessors';
import { sleep } from 'global/utils';
import { isDesktop } from 'app/utils/resolution';
import { ONE_DAYS_IN_UTC_FORMAT } from './contexts/menuDates.consts';

// Ставит X + Day в зависимости от города BR-1019
const getCityCtxDateExtender = () => {
    const addressSession = apolloClient.readFragment({
        id: 'address:session_address', fragment: CONTRY_SUBDIVISION_FRAGMENT,
    });
    if (!addressSession) {
        return 0;
    }
    const { country_subdivision } = addressSession;
    return ['RU-MOS', 'RU-MOW'].includes(country_subdivision) ? 0 : ONE_DAYS_IN_UTC_FORMAT;
};

function getDishesQueryVariables(selectedFilters, menuFilter) {
    const { selectedPeriod } = selectedFilters;
    const period = menuFilter?.periods?.find((p) => p.start === selectedPeriod);
    return {
        period: selectedPeriod,
        periodObj: period,
        // type: parseMenuType(window.location),
        tags: period?.tags?.map((tag) => tag.id) || [],
    };
}

function selectMenuDishes(
    allDishes,
    basketDishes,
    tags,
    categoryIds,
    eeAvailableDates,
    dateFilterStatus,
) {
    const isDesktopResolution = isDesktop();
    const basketDishIds = basketDishes.map((dish) => dish.id);

    // BR-1104
    const allDishesWithoutCustom = !isDesktopResolution
        ? allDishes
        : allDishes.filter((dish) => {
            const isInBasket = basketDishes.filter((basketDish) => (
                basketDish.parentId === dish.parentId && basketDish.secondDishPart === dish.secondDishPart
            ));
            return !isInBasket.length;
        });

    const nonBasketDishes = allDishesWithoutCustom.filter((dish) => {
        const isDishSavedInBasket = basketDishIds.includes(dish.id);
        return !isDishSavedInBasket;
    });

    const cityCtxDateExtender = getCityCtxDateExtender();

    return {
        menuDishes: selectDishesByFilter(
            isDesktopResolution ? nonBasketDishes : allDishesWithoutCustom,
            null,
            tags,
            categoryIds,
            basketDishes,
            eeAvailableDates,
            dateFilterStatus,
            cityCtxDateExtender,
            isDesktopResolution,
        ),
        filtredDishes: selectDishesByFilter(allDishes, null, tags, categoryIds),
        allDishes: nonBasketDishes,
    };
}


const emptySections = [];

const applyFilters = ({ menuDishes }, period) => menuDishes.filter((d) => {
    const dishTagIds = d.tags ? d.tags.map((t) => t.id) : [];
    if (!period) return false;

    if (period.excludeTags) {
        const dishHasExcludeTags = dishTagIds.find((dishTag) => period.excludeTags.includes(dishTag));
        return !dishHasExcludeTags;
    }
    if (period.includeTagsOnly) {
        const dishHasIncludeTags = dishTagIds.find((dishTag) => period.includeTagsOnly.includes(dishTag));
        return dishHasIncludeTags;
    }

    return true;
});

export default function MenuDishesProvider(
    {
        render,
        selectedPeriod,
        setDishesUpdatingState = () => { },
        fetchPolicy = 'cache-first',
        wrapper,
    },
) {
    const appPeriod = useReactiveVar(selectedPeriodVar);
    const subdivision = useReactiveVar(regionSubdivisionVar);

    // DA CACHED DATA
    const [basketQuery] = useBasketQueryLocate();
    const selectedFilters = useSelectedFilters();
    // const maxiBlockContentKey = useReactiveVar(maxiBlockContentKeyVar);

    const menuFilter = useMenuPeriodsData();
    const [dishesData, setDishesData] = useState([]);

    const sections = basketQuery?.cart?.sections ?? emptySections;

    // TODO: костыль для корзины доставки
    const filters = selectedPeriod
        ? { selectedPeriod, selectedCategories: DEFAULT_CATEGORIES, selectedTags: [] }
        : selectedFilters;

    const { tags, periodObj: period } = getDishesQueryVariables(filters, menuFilter);

    const hash = useMemo(
        () => `${appPeriod}:${subdivision}`,
        [appPeriod, subdivision],
    );

    const [fetchMenuDishes, { loading }] = useLazyQuery(ROOT_MENU_DISHES_LIST, {
        context: {
            message: 'root:init:MenuDishesProvider',
        },
    });

    const getMenuDieshesData = () => {
        setDishesUpdatingState(true);
        fetchMenuDishes({
            variables: {
                period: appPeriod,
                tags,
            },
            fetchPolicy: 'cache-first',
        }).then(({ data }) => {
            const dishes = applyFilters(data, period);
            setDishesData(dishes);
            allDishesListVar(dishes);
            sleep(600).then(() => {
                setDishesUpdatingState(false);
            });
        });
    };

    useEffect(() => {
        getMenuDieshesData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hash]);


    useEffect(() => {

    }, [setDishesUpdatingState]);

    const {
        state: {
            eeAvailableDates,
            dateFilterStatus,
            filterPeriod: menuDatesStateFilterPeriod,
        },
    } = useContext(menuDatesState);

    const basketDishes = useMemo(() => getDishesFromBasketSections(sections), [sections]);

    const { selectedTags, selectedCategories } = filters;

    const prepDishes = () => {
        let responseDishes;
        if (menuDatesStateFilterPeriod !== filters.selectedPeriod) {
            responseDishes = selectMenuDishes(dishesData, basketDishes, selectedTags, selectedCategories);
        } else {
            responseDishes = selectMenuDishes(
                dishesData, basketDishes, selectedTags, selectedCategories, eeAvailableDates, dateFilterStatus,
            );
        }

        return responseDishes;
    };

    const {
        menuDishes,
        allDishes,
        filtredDishes,
    } = prepDishes();

    // const {

    // } = useMemo(() => {

    // }, [
    //     dishesData,
    //     basketDishes,
    //     selectedTags,
    //     selectedCategories,
    //     eeAvailableDates,
    //     dateFilterStatus,
    //     menuDatesStateFilterPeriod,
    //     filters.selectedPeriod,
    // ]);

    // const menuDishesQuery = {
    //     loading: true,
    //     menuDishes: all,
    //     allDishes: all,
    //     basketDishes: all,
    //     filtredDishes: all,
    //     refetch: () => { alert(this); },
    // };


    const menuDishesQuery = {
        loading,
        menuDishes,
        allDishes,
        filtredDishes,
        basketDishes,
        refetch: () => { console.error('refetch, from menuDishesQuery'); },
    };

    useEffect(() => {
        const basket_IDs = basketDishes?.map((itm) => itm.id) || [];
        const menu_IDs = menuDishes?.map((itm) => itm.id) || [];
        menuDishesIdListVar([...basket_IDs, ...menu_IDs]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuDishes?.length, basketDishes?.length]);

    const field = menuDishesQuery?.menuDishes?.length ? 'menuDishes' : 'filtredDishes';

    return render({
        menuDishesQuery: {
            ...menuDishesQuery,
            menuDishes: menuDishesQuery[field]?.filter((d) => d.id !== '5332'),
        },
        selectedFilters,
        hasDataForRenderDishes: dishesData.length > 0,
    });
}

MenuDishesProvider.propTypes = {
    render: PropTypes.func.isRequired,
    setDishesUpdatingState: PropTypes.func,
    fetchPolicy: PropTypes.string,
};
